import { IoIosAddCircleOutline } from "react-icons/io";
import { TiMinus, TiPlus } from "react-icons/ti";
import { Switch } from "react-animated-switch";
import styles from "./Addons.module.scss";
import React from "react";

interface AddonItem {
  addonId: number;
  amenityName: string;
  noofAddonunits: number;
  availableNoofAddonunits: number;
  cost: number;
  terms: string;
  image: string;
  amenityId: number;
}

interface AddonSelection {
  addonId: number;
  noofAddonUnits: number;
  notifyAddon: boolean;
}

interface Props {
  addons: AddonItem[];
  addonSelections: AddonSelection[];
  setAddonSelections: React.Dispatch<React.SetStateAction<AddonSelection[]>>;
}

function Addons(props: Props) {
  const { addons, addonSelections, setAddonSelections } = props;

  const getSel = (addonId: number) =>
    addonSelections.find((s) => s.addonId === addonId) ?? {
      addonId,
      noofAddonUnits: 0,
      notifyAddon: false,
    };

  const updateSel = (addonId: number, patch: Partial<AddonSelection>) => {
    setAddonSelections((prev) => {
      const i = prev.findIndex((s) => s.addonId === addonId);

      const cur =
        i >= 0
          ? prev[i]
          : {
              addonId,
              noofAddonUnits: 0,
              notifyAddon: false,
            };

      const next = { ...cur, ...patch };

      return i >= 0
        ? prev.map((s, j) => (j === i ? next : s))
        : [...prev, next];
    });
  };

  const changeCount = (opt: AddonItem, delta: number) => {
    const s = getSel(opt.addonId);
    const max = opt.availableNoofAddonunits ?? 0;
    const next = s.noofAddonUnits + delta;

    if (next >= 0 && next <= max) {
      updateSel(opt.addonId, { noofAddonUnits: next });
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <p className={styles.heading}>Addons</p>
        <p className={styles.subHeading}>Select addons</p>
      </div>

      {addons?.map((opt, index) => {
        const s = getSel(opt.addonId);
        const noneAvailable = (opt.availableNoofAddonunits ?? 0) === 0;
        const addonAvailable = !noneAvailable;
        const hasSelection = s.noofAddonUnits > 0 || s.notifyAddon;

        return (
          <div
            key={index}
            className={`${styles.parkingGroup} ${hasSelection ? styles.userAddedParking : ""}`}
          >
            <div className={styles.iconContainer}>
              <IoIosAddCircleOutline
                className={`${styles.icon} ${addonAvailable ? styles.parkingAvailableIcon : ""}`}
              />
            </div>
            <div className={styles.parkingContentMainContainer}>
              <div className={styles.parkingContentContainer}>
                <div className={styles.labelContainer}>
                  <p className={styles.label}>{opt.amenityName}</p>
                  {noneAvailable && (
                    <div className={styles.notAvailableContainer}>
                      <p className={styles.label}>Unavailable</p>
                    </div>
                  )}
                </div>

                <div className={styles.selectContainer}>
                  {!noneAvailable ? (
                    <>
                      <div className={styles.availableSpacesContainer}>
                        <p className={styles.spaceText}>
                          {opt.availableNoofAddonunits ?? 0} Units Available
                        </p>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.bar}>
                          <div
                            className={styles.barFill}
                            style={{
                              width: `${((opt.availableNoofAddonunits ?? 0) / (opt.noofAddonunits ?? 0)) * 100}%`,
                            }}
                          ></div>
                        </div>
                        <p className={styles.barText}>
                          {opt.availableNoofAddonunits ?? 0} /{" "}
                          {opt.noofAddonunits ?? 0} Units
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className={styles.noticeContainer}>
                      <div className={styles.switchContainer}>
                        <Switch
                          inActiveTrackColor="#DFDFDF"
                          activeTrackColor="#C0F496"
                          inActiveThumbColor="#000000"
                          activeThumbColor="#000000"
                          checked={s.notifyAddon}
                          onChange={(val) =>
                            updateSel(opt.addonId, {
                              notifyAddon: val,
                            })
                          }
                        />
                      </div>
                      <p className={styles.label}>
                        Notify me when addon is available
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!noneAvailable && (
              <div className={styles.counterContainer}>
                <button
                  className={styles.counterButton}
                  onClick={() => changeCount(opt, -1)}
                >
                  <TiMinus />
                </button>
                <p className={styles.counterValue}>{s.noofAddonUnits}</p>
                <button
                  className={styles.counterButton}
                  onClick={() => changeCount(opt, 1)}
                >
                  <TiPlus />
                </button>
              </div>
            )}

            <div className={styles.priceContainer}>
              <p>${(opt.cost ?? 0) * s.noofAddonUnits}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Addons;
